export interface ReadonlyStore<T> {
  get(): T;
  /** Register a listener called after every state change. Returns an unsubscribe function. */
  subscribe(listener: () => void): () => void;
}

export interface Store<T> extends ReadonlyStore<T> {
  /** Replace the state with a value or an updater. An `Object.is`-equal result is a no-op. */
  set(updater: T | ((prev: T) => T)): void;
}

/** Create a minimal observable store. See ./README.md for usage and migration guidance. */
export function createStore<T>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<() => void>();

  return {
    get: () => state,

    set: (updater) => {
      const next = typeof updater === "function" ? (updater as (prev: T) => T)(state) : updater;
      if (Object.is(next, state)) return;
      state = next;
      [...listeners].forEach((listener) => listener());
    },

    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
